import { z } from 'zod';
const copy = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !/[<>\u0000-\u001f]/.test(value), 'Use plain text');
export const brandingSchema = z
  .object({
    siteName: copy(80),
    calendarTagline: copy(200),
    loginHeading: copy(100),
    loginDescription: copy(300),
    eventBackLabel: copy(60),
    colorPreset: z.enum(['forest', 'ocean', 'plum', 'thornton']),
    fontPreset: z.enum(['classic', 'modern', 'humanist']),
  })
  .strict();
export type BrandingConfig = z.infer<typeof brandingSchema>;
export const DEFAULT_BRANDING: BrandingConfig = {
  siteName: 'Thorton Creek Parent Group',
  calendarTagline: 'Meetings, celebrations, playdates, and everything in between.',
  loginHeading: 'Welcome back',
  loginDescription: 'Sign in to manage calendars or use family-only calendar access.',
  eventBackLabel: 'Back to calendar',
  colorPreset: 'thornton',
  fontPreset: 'classic',
};
export const BRANDING_COLORS = {
  forest: {
    label: 'Forest & cream',
    cream: '#fbf6e9',
    paper: '#fffdf7',
    paperDeep: '#f3ead7',
    primary: '#234c3b',
    dark: '#18362c',
    soft: '#dce9df',
    primaryLight: '#9dc9ac',
    primaryPale: '#d7eadc',
    accent: '#b94d2b',
    accentDark: '#963b20',
    accentLight: '#efad91',
  },
  ocean: {
    label: 'Ocean & mist',
    cream: '#edf5f7',
    paper: '#fbfdfe',
    paperDeep: '#dfebef',
    primary: '#24546a',
    dark: '#173e50',
    soft: '#deebef',
    primaryLight: '#9fc7d6',
    primaryPale: '#d7eaf1',
    accent: '#a24e24',
    accentDark: '#7d3518',
    accentLight: '#f0a67e',
  },
  plum: {
    label: 'Plum & linen',
    cream: '#f6eff5',
    paper: '#fffbff',
    paperDeep: '#eee1ed',
    primary: '#624264',
    dark: '#442d46',
    soft: '#ede2ee',
    primaryLight: '#d3b4d5',
    primaryPale: '#eeddf0',
    accent: '#994728',
    accentDark: '#75331c',
    accentLight: '#e7ab91',
  },
  thornton: {
    label: 'Thornton purple & green',
    cream: '#f8f6fa',
    paper: '#fffdfd',
    paperDeep: '#eee8f2',
    primary: '#3d704b',
    dark: '#284d34',
    soft: '#deeddc',
    primaryLight: '#9fd5a8',
    primaryPale: '#d9f0d8',
    accent: '#76529a',
    accentDark: '#573970',
    accentLight: '#d9b8f2',
  },
} as const;
export const BRANDING_FONTS = {
  classic: {
    label: 'Classic',
    body: 'Inter, ui-sans-serif, system-ui, sans-serif',
    heading: "Georgia, 'Times New Roman', serif",
  },
  modern: {
    label: 'Modern',
    body: 'ui-sans-serif, system-ui, sans-serif',
    heading: 'ui-sans-serif, system-ui, sans-serif',
  },
  humanist: {
    label: 'Humanist',
    body: "'Trebuchet MS', ui-sans-serif, sans-serif",
    heading: "'Palatino Linotype', Palatino, Georgia, serif",
  },
} as const;
export type BrandingState = {
  draft: BrandingConfig;
  published: BrandingConfig;
  previous: BrandingConfig | null;
  draftVersion: number;
  version: number;
};
